import { useEffect, useState } from 'react';

import { StepButton } from 'features/shop/cart/EBTPaymentFlow/shared/FlowPresentation';
import { getAmountErrorMessage } from 'features/shop/cart/EBTPaymentFlow/shared/utils';
import { EBTFormSubmissionError } from 'shared/ebt/EBTErrors';
import EBTPaymentElement from 'shared/ebt/EBTPaymentElement';
import ForagePinElement from 'shared/ebt/ForagePinElement';
import twclsx from 'utils/twclsx';

/**
 * EBT payment form component with PIN validation and optional amount field.
 * Supports both payment processing and balance checking functionality.
 */
function EBTPaymentForm({
    amountCurrent = 0,
    amountDefault,
    amountLabel = 'Payment Amount',
    amountSnapBalance,
    amountSnapEligibleTotal,
    balanceCheckError,
    className = '',
    error,
    isAmountFieldShown = true,
    isBalanceButtonShown = false,
    isBalanceLoading = false,
    isPaymentProcessing = false,
    onCheckBalanceClick,
    onSubmit,
    pinElementID = 'ebt-payment-pin-element',
    submitButtonText = 'Submit',
}) {
    const [pinField, setPinField] = useState({
        element: null,
        isValid: false,
        showFieldError: false,
    });

    const [amountField, setAmountField] = useState({
        value: amountDefault || '',
        isValid: false,
        error: '',
    });

    useEffect(() => {
        setAmountField((prev) => ({ ...prev, value: amountDefault || '' }));
    }, [amountDefault]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        // Clear previous errors
        setPinField((prev) => ({ ...prev, showFieldError: false }));
        setAmountField((prev) => ({ ...prev, error: '' }));

        // Validate PIN field
        const isPinInvalid = !pinField.isValid;
        if (isPinInvalid) {
            setPinField((prev) => ({ ...prev, showFieldError: true }));
        }

        // Validate amount field if present
        let isAmountInvalid = false;
        if (isAmountFieldShown) {
            const amountError = getAmountErrorMessage({
                amount: parseFloat(amountField.value),
                snapEligibleTotal: amountSnapEligibleTotal,
                snapBalance: amountSnapBalance,
                currentAmount: amountCurrent,
            });

            if (amountError) {
                setAmountField((prev) => ({ ...prev, error: amountError }));
                isAmountInvalid = true;
            }
        }

        // If either field is invalid, don't proceed
        if (isPinInvalid || isAmountInvalid) {
            return;
        }

        // Call the parent's onSubmit with the form data
        await onSubmit({
            pinElement: pinField.element,
            amount: isAmountFieldShown ? Number(amountField.value) : null,
        });
    };

    const handlePinChange = (_, isValid) => {
        setPinField((prev) => ({ ...prev, isValid, showFieldError: false }));
    };

    const handlePinReady = (element) => {
        setPinField((prev) => ({ ...prev, element, showFieldError: false }));
    };

    const handleAmountChange = (value) => {
        setAmountField((prev) => ({
            ...prev,
            value,
            error: '', // Clear error when user types
        }));
    };

    return (
        <form onSubmit={handleFormSubmit} className={twclsx('space-y-15', className)}>
            <div className={twclsx('mb-10 mt-30', isAmountFieldShown && 'md:flex md:items-start md:gap-15')}>
                <div className={twclsx(isAmountFieldShown && 'mb-25 md:mb-0 md:basis-1/2')}>
                    <ForagePinElement
                        elementID={pinElementID}
                        showBalanceButton={isBalanceButtonShown}
                        balanceButtonProps={{
                            onBalanceCheck: () => onCheckBalanceClick(pinField.element),
                            isBalanceLoading,
                        }}
                        onChange={handlePinChange}
                        onReady={handlePinReady}
                        error={balanceCheckError || pinField.showFieldError}
                    />
                </div>
                {isAmountFieldShown && (
                    <div className="md:-mt-10 md:basis-[49%]">
                        <EBTPaymentElement
                            defaultAmount={amountField.value}
                            onChange={handleAmountChange}
                            label={amountLabel}
                            error={amountField.error}
                        />
                    </div>
                )}
            </div>
            <StepButton type="submit" disabled={isPaymentProcessing}>
                {submitButtonText}
            </StepButton>
            {error && <EBTFormSubmissionError className="space-y-5 text-center" error={error} />}
        </form>
    );
}

export default EBTPaymentForm;
